/* ILUMAA cart drawer behaviour: gift picker, cross-sell fallback, EU-notice dialog. ES module, no dependencies. */

function root() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

function refreshCart() {
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
}

function toastError(message) {
  document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: message, tone: 'error' } }));
}

async function cartUpdate(updates) {
  const response = await fetch(root() + 'cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates: updates }),
  });
  if (!response.ok) throw new Error('cart/update.js failed');
  return response.json();
}

async function cartAdd(id, properties) {
  const response = await fetch(root() + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: id, quantity: 1, properties: properties }] }),
  });
  if (!response.ok) throw new Error('cart/add.js failed');
  return response.json();
}

class IlumaaGiftPicker extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
    this.guard();
  }

  getLines() {
    return Array.from(this.querySelectorAll('.ilumaa-gift-picker__gift-line')).map((el) => ({
      key: el.dataset.key,
      variantId: el.dataset.variantId,
      quantity: parseInt(el.dataset.quantity, 10) || 0,
    }));
  }

  async guard() {
    if (this.busy || !this.isConnected) return;
    const qualified = this.dataset.qualified === 'true';
    const lines = this.getLines();

    if (!qualified && lines.length > 0) {
      await this.applyUpdates(lines.map((l) => [l.key, 0]));
      return;
    }

    if (qualified && lines.length > 0) {
      const extra = lines.slice(1).filter((l) => l.quantity !== 0);
      const wrongQuantity = lines[0] && lines[0].quantity !== 1;
      if (extra.length > 0 || wrongQuantity) {
        const updates = extra.map((l) => [l.key, 0]);
        if (wrongQuantity) updates.push([lines[0].key, 1]);
        await this.applyUpdates(updates);
      }
    }
  }

  async applyUpdates(pairs) {
    if (pairs.length === 0 || this.busy) return;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const updates = {};
      pairs.forEach(([key, quantity]) => {
        updates[key] = quantity;
      });
      await cartUpdate(updates);
      refreshCart();
    } catch (error) {
      toastError('Dein Geschenk konnte nicht aktualisiert werden. Bitte versuche es erneut.');
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  async onClick(event) {
    const button = event.target.closest('.ilumaa-gift-card__button');
    if (!button || button.disabled || this.busy) return;
    if (button.classList.contains('ilumaa-gift-card__button--selected')) return;

    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const existing = this.getLines();
      if (existing.length > 0) {
        const updates = {};
        existing.forEach((l) => {
          updates[l.key] = 0;
        });
        await cartUpdate(updates);
      }
      await cartAdd(button.dataset.variantId, { _ilumaa_gift: '1' });
      refreshCart();
    } catch (error) {
      toastError('Dein Geschenk konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
      button.disabled = false;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

class IlumaaCrossSell extends HTMLElement {
  async connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';

    const productId = this.dataset.productId;
    if (!productId) return;

    const sectionId = this.dataset.sectionId;
    const limit = this.dataset.limit || '10';
    const base = `${root()}recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}`;

    const complementary = await this.fetchIntent(`${base}&intent=complementary`);
    if (this.apply(complementary)) return;

    const related = await this.fetchIntent(`${base}&intent=related`);
    this.apply(related);
  }

  async fetchIntent(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.getElementById(this.id);
    } catch (error) {
      return null;
    }
  }

  apply(fetchedElement) {
    if (!fetchedElement || fetchedElement.childElementCount === 0) return false;
    this.replaceChildren(...document.importNode(fetchedElement, true).childNodes);
    this.hidden = false;
    return true;
  }
}

/*
 * EU guarantee notice: a plain in-drawer overlay (not a native <dialog>), because <dialog>.showModal()
 * promotes the element to the top layer, outside the Stretch drawer's own stacking context — this
 * conflicts with the drawer (closes it, hides the image). Toggled via the [hidden] attribute instead.
 * All listeners are delegated on `document` so they survive the drawer's replaceChildren re-renders.
 */

function openEuNotice(dialog, trigger) {
  dialog.ilumaaTrigger = trigger || null;
  dialog.hidden = false;
  const closeButton = dialog.querySelector('.ilumaa-eu-notice__close');
  if (closeButton) closeButton.focus();
}

function closeEuNotice(dialog) {
  dialog.hidden = true;
  if (dialog.ilumaaTrigger) dialog.ilumaaTrigger.focus();
}

function findOpenEuNotice() {
  return document.querySelector('.ilumaa-eu-notice:not([hidden])');
}

function initEuNotices() {
  // Capture phase: runs before Stretch's own document-level bubble listeners (drawer outside-click /
  // ESC-to-close), so stopPropagation() here reliably keeps the drawer open while the overlay is open.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const openDialog = findOpenEuNotice();
      if (!openDialog) return;
      event.stopPropagation();
      closeEuNotice(openDialog);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const opener = event.target.closest('[data-ilumaa-eu-dialog-open]');
      if (opener) {
        const dialog = document.getElementById(opener.getAttribute('aria-controls'));
        if (dialog) openEuNotice(dialog, opener);
        return;
      }

      const notice = event.target.closest('.ilumaa-eu-notice');
      if (!notice || notice.hidden) return;

      // Any click inside the (open) overlay — backdrop, panel or close button — must not bubble to the
      // drawer's own outside-click handler.
      event.stopPropagation();

      if (event.target.closest('[data-ilumaa-eu-dialog-close]')) closeEuNotice(notice);
    },
    true
  );
}

if (!window.customElements.get('ilumaa-gift-picker')) {
  window.customElements.define('ilumaa-gift-picker', IlumaaGiftPicker);
}

if (!window.customElements.get('ilumaa-cross-sell')) {
  window.customElements.define('ilumaa-cross-sell', IlumaaCrossSell);
}

initEuNotices();

/*
 * Mengenrabatt: product page tiles (<ilumaa-qty-breaks>) and cart drawer nudge (<ilumaa-qty-nudge>).
 * Tier thresholds must match snippets/ilumaa-qty-breaks.liquid and snippets/line-item.liquid.
 */

const QTY_TIER_A = 2;
const QTY_TIER_B = 3;

class IlumaaQtyBreaks extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onQuantityEvent = this.onQuantityEvent.bind(this);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('input', this.onQuantityEvent);
    document.addEventListener('change', this.onQuantityEvent);

    const input = this.getQuantityInput();
    if (input) this.select(this.tileIndexFor(parseInt(input.value, 10)));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('input', this.onQuantityEvent);
    document.removeEventListener('change', this.onQuantityEvent);
  }

  getQuantityInput() {
    const formId = this.getAttribute('form-id');
    if (!formId) return null;
    const form = document.getElementById(formId);
    if (form) {
      const inside = form.querySelector('input[name="quantity"]');
      if (inside) return inside;
    }
    return Array.from(document.querySelectorAll('input[name="quantity"][form]')).find((el) => el.getAttribute('form') === formId) || null;
  }

  get tiles() {
    return Array.from(this.querySelectorAll('[role="radio"]'));
  }

  tileIndexFor(quantity) {
    if (quantity >= QTY_TIER_B) return 2;
    if (quantity === QTY_TIER_A) return 1;
    return 0;
  }

  select(index) {
    this.tiles.forEach((tile, i) => {
      tile.setAttribute('aria-checked', i === index ? 'true' : 'false');
      tile.setAttribute('tabindex', i === index ? '0' : '-1');
    });
  }

  applyQuantity(tile) {
    const input = this.getQuantityInput();
    if (!input) return;
    input.value = tile.dataset.qty;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onClick(event) {
    const tile = event.target.closest('[role="radio"]');
    if (!tile || !this.contains(tile)) return;
    this.select(this.tiles.indexOf(tile));
    this.applyQuantity(tile);
  }

  onKeydown(event) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    const tiles = this.tiles;
    const current = tiles.findIndex((tile) => tile === document.activeElement);
    if (current === -1) return;
    event.preventDefault();
    const next = (current + keys[event.key] + tiles.length) % tiles.length;
    tiles[next].focus();
    this.select(next);
    this.applyQuantity(tiles[next]);
  }

  onQuantityEvent(event) {
    const input = this.getQuantityInput();
    if (!input || event.target !== input) return;
    this.select(this.tileIndexFor(parseInt(input.value, 10)));
  }
}

class IlumaaQtyNudge extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
    const increase = this.getIncreaseControl();
    // Quantity limit reached: the theme renders a disabled button instead of a link, so the nudge is pointless.
    if (increase && increase.matches('button[disabled]')) this.hidden = true;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  getIncreaseControl() {
    const lineItem = this.closest('line-item');
    if (!lineItem) return null;
    const controls = lineItem.querySelectorAll('.quantity-selector__button');
    return controls.length ? controls[controls.length - 1] : null;
  }

  onClick(event) {
    const link = event.target.closest('a, button');
    if (!link || !this.contains(link)) return;
    const increase = this.getIncreaseControl();
    if (!increase) return; // no JS control available: let the fallback link run
    event.preventDefault();
    increase.click();
  }
}

if (!window.customElements.get('ilumaa-qty-breaks')) {
  window.customElements.define('ilumaa-qty-breaks', IlumaaQtyBreaks);
}

if (!window.customElements.get('ilumaa-qty-nudge')) {
  window.customElements.define('ilumaa-qty-nudge', IlumaaQtyNudge);
}
